/**
 * MonitorsPage.tsx — /app/monitors — full CRUD monitor management
 *
 * Purpose:    Table of uptime monitors with add/edit/delete, interval limited
 *             to the signed-in tier's floor, and a usage line with upgrade CTA.
 * Inputs:     monitorsApi + billingApi from @/lib/saas-client
 * Outputs:    CRUD UI over Monitor records
 * Constraints: Dark-first styling; WCAG AA (labels, focus-visible, aria)
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { monitorsApi, billingApi, type Monitor } from '@/lib/saas-client'
import { TIERS, allowedIntervalMinutes, usagePct, type TierId } from '@/lib/tiers'
import type { SentryError } from '@/lib/sentry-error'

const STATUS_STYLES: Record<Monitor['status'], string> = {
  up: 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  down: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  degraded: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
  paused: 'bg-brand-surface-2 text-brand-muted',
  pending: 'bg-brand-surface-2 text-brand-muted',
}

interface FormState {
  name: string
  url: string
  intervalMinutes: number
}

const EMPTY_FORM: FormState = { name: '', url: '', intervalMinutes: 5 }

export function MonitorsPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [monitors, setMonitors] = useState<Monitor[]>([])
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)

  async function load() {
    setState('loading')
    const [monitorsRes, billingRes] = await Promise.all([monitorsApi.list(), billingApi.get()])
    if (!monitorsRes.ok) {
      setError(monitorsRes.error)
      setState('error')
      return
    }
    setMonitors(monitorsRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState(monitorsRes.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  const quota = TIERS[tier].monitors
  const intervalOptions = allowedIntervalMinutes(tier)
  const pct = usagePct(monitors.length, quota.max)

  function openCreate() {
    setEditingId(null)
    setForm({ ...EMPTY_FORM, intervalMinutes: intervalOptions[0] })
    setFormError(null)
    setShowForm(true)
  }

  function openEdit(m: Monitor) {
    setEditingId(m.id)
    setForm({ name: m.name, url: m.url, intervalMinutes: m.intervalSeconds / 60 })
    setFormError(null)
    setShowForm(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.url.startsWith('http://') && !form.url.startsWith('https://')) {
      setFormError('URL must start with http:// or https://')
      return
    }
    const intervalSeconds = form.intervalMinutes * 60
    const result = editingId
      ? await monitorsApi.update(editingId, { name: form.name, url: form.url, intervalSeconds })
      : await monitorsApi.create({ name: form.name, url: form.url, intervalSeconds })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setShowForm(false)
    await load()
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this monitor?')) return
    await monitorsApi.remove(id)
    await load()
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Monitors</h1>
          <p className="mt-1 text-sm text-brand-muted">
            {monitors.length} / {quota.max} monitors ({TIERS[tier].label})
            {pct >= 80 && (
              <>
                {' '}
                —{' '}
                <Link to="/app/billing" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
                  upgrade for more
                </Link>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
        >
          Add monitor
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6"
          aria-label={editingId ? 'Edit monitor' : 'Add monitor'}
        >
          {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="mon-name" className="mb-1 block text-sm font-medium text-brand-secondary">Name</label>
              <input
                id="mon-name"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="mon-url" className="mb-1 block text-sm font-medium text-brand-secondary">URL</label>
              <input
                id="mon-url"
                required
                type="url"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://example.com"
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="mon-interval" className="mb-1 block text-sm font-medium text-brand-secondary">Check interval</label>
              <select
                id="mon-interval"
                value={form.intervalMinutes}
                onChange={(e) => setForm((f) => ({ ...f, intervalMinutes: Number(e.target.value) }))}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
              >
                {intervalOptions.map((m) => (
                  <option key={m} value={m}>{m} min</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
              {editingId ? 'Save changes' : 'Create monitor'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <AsyncScreen
        state={state}
        error={error}
        onRetry={load}
        emptyMessage="No monitors yet — add one"
        emptyAction={
          <button type="button" onClick={openCreate} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500">
            Add your first monitor
          </button>
        }
      >
        <div className="overflow-x-auto rounded-xl border border-brand-subtle">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-surface text-brand-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">Name</th>
                <th scope="col" className="px-4 py-3 font-medium">URL</th>
                <th scope="col" className="px-4 py-3 font-medium">Interval</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 font-medium">Latency</th>
                <th scope="col" className="px-4 py-3 font-medium">Last check</th>
                <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800 bg-brand-bg">
              {monitors.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-3 font-medium text-brand-ink">
                    <Link to={`/app/monitors/${m.id}`} className="hover:text-sky-700 dark:text-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
                      {m.name}
                    </Link>
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-brand-muted">{m.url}</td>
                  <td className="px-4 py-3 text-brand-muted">{m.intervalSeconds / 60} min</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[m.status]}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-3 text-brand-muted">{m.latencyMs != null ? `${m.latencyMs}ms` : '—'}</td>
                  <td className="px-4 py-3 text-brand-muted">{m.lastCheckedAt ? new Date(m.lastCheckedAt).toLocaleTimeString() : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => openEdit(m)} className="mr-3 text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(m.id)} className="text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncScreen>
    </div>
  )
}
